import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, User, ShieldAlert, Users, PhoneOff } from 'lucide-react';
import { liveAPI } from '../api/client';
import MeetingRoomPlayer from '../components/MeetingRoomPlayer';

export default function MeetingJoinPage() {
    const { roomId } = useParams();
    const navigate = useNavigate();

    const [hasJoined, setHasJoined] = useState(false);
    const [name, setName] = useState('');
    const [secretCode, setSecretCode] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [error, setError] = useState(null);

    // Initial check: if there is a profile, pre-fill name
    React.useEffect(() => {
        try {
            const profile = JSON.parse(localStorage.getItem('customer_profile'));
            if (profile?.name) setName(profile.name);
        } catch (e) { }
    }, []);

    const handleJoin = async (e) => {
        e.preventDefault();
        setError(null);
        setIsJoining(true);

        try {
            // Verify secret code by attempting to get participants (this might fail if backend restricts it, but we actually do it inside the player)
            // Wait, we just try to join.
            await liveAPI.joinRoom(roomId, name, false, secretCode);
            setHasJoined(true);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Invalid Secret Code or Room is Full.');
        } finally {
            setIsJoining(false);
        }
    };

    if (hasJoined) {
        return <MeetingRoomPlayer roomId={roomId} secretCode={secretCode} myUserName={name} onLeave={() => navigate('/')} />;
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--lux-bg, #050508)' }}>
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#050508]">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500/10 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[40%] rounded-full bg-purple-500/10 blur-[150px]" />
            </div>

            <div className="w-full max-w-md bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2rem] shadow-2xl relative z-10">
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                        <Users size={32} className="text-blue-400" />
                    </div>
                </div>
                <h1 className="text-2xl font-bold text-center text-white mb-2">Join Private Meeting</h1>
                <p className="text-gray-400 text-center mb-8 text-sm">Enter your details to join Meeting #{roomId}</p>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl mb-6 text-sm flex items-start gap-3">
                        <ShieldAlert size={18} className="shrink-0 mt-0.5" />
                        <p>{error}</p>
                    </div>
                )}

                <form onSubmit={handleJoin} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">Your Name</label>
                        <div className="relative">
                            <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-11 pr-4 text-white focus:outline-none focus:border-blue-500 transition"
                                placeholder="John Doe"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5 ml-1">Secret Code</label>
                        <div className="relative">
                            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                required
                                value={secretCode}
                                onChange={(e) => setSecretCode(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-11 pr-4 text-white focus:outline-none focus:border-blue-500 transition font-mono tracking-widest uppercase"
                                placeholder="ENTER CODE"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={!name || !secretCode || isJoining}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl transition shadow-lg shadow-blue-500/20 disabled:opacity-50 mt-4"
                    >
                        {isJoining ? 'Verifying...' : 'Join Meeting'}
                    </button>
                    
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="w-full bg-transparent border border-white/10 hover:bg-white/5 text-gray-300 font-medium py-3 rounded-xl transition flex items-center justify-center gap-2"
                    >
                        <PhoneOff size={18} /> Cancel
                    </button>
                </form>
            </div>
        </div>
    );
}
